import { bilateralPnlAmount, monthlyPnlMatrixAmount, zeroBilateralPnlCell, type MonthlyPnlMatrixCell } from "@chro/shared";

export type PnlOverviewMode = "cards" | "comparison" | "overview" | "bridge";
export const pnlOverviewModes: { value: PnlOverviewMode; label: string }[] = [
  { value: "cards", label: "Total-first cards" }, { value: "comparison", label: "Comparison table" },
  { value: "overview", label: "Month overview" }, { value: "bridge", label: "Contribution bridge" },
];
export const pnlMoney = (value: number | null) => value === null ? "Unavailable" : new Intl.NumberFormat("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value === 0 ? 0 : value);
export const pnlCompact = (value: number | null) => value === null ? "—" : new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 2 }).format(value === 0 ? 0 : value);
export const pnlMonthLabel = (month: string) => new Date(`${month}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
const sum = (...values: (number | null)[]) => values.some(value => value === null) ? null : values.reduce<number>((total, value) => total + Math.round(value! * 100), 0) / 100;

/** The existing strategy basis always includes cleared and bilateral forecasts.
 * The estimates switch only controls cleared-only results and source columns. */
export function monthlyPnlSummary(cell: MonthlyPnlMatrixCell, includeEstimates: boolean, includeBilateral: boolean) {
  const forecasts = includeEstimates || includeBilateral;
  const bilateral = { ...zeroBilateralPnlCell(), ...cell.bilateral };
  const clearedMissing = cell.finalMissingCount + (forecasts ? cell.estimateMissingCount : 0);
  const cleared = clearedMissing ? null : monthlyPnlMatrixAmount(cell, forecasts);
  const physicalKnown = bilateral.physicalMissingCount ? null : bilateralPnlAmount(bilateral, "physical");
  const otcKnown = bilateral.otcMissingCount ? null : bilateralPnlAmount(bilateral, "otc");
  const physical = bilateral.unclassifiedCount && physicalKnown === 0 ? null : physicalKnown;
  const otc = bilateral.unclassifiedCount && otcKnown === 0 ? null : otcKnown;
  const total = includeBilateral ? bilateral.unclassifiedCount ? null : sum(cleared, physical, otc) : cleared;
  const estimated = forecasts && (cell.estimateEUR !== 0 || cell.estimateMissingCount > 0 || cell.staleCount > 0)
    || includeBilateral && (bilateral.physicalEstimateEUR !== 0 || bilateral.otcEstimateEUR !== 0);
  const missing = clearedMissing + (includeBilateral ? bilateral.physicalMissingCount + bilateral.otcMissingCount : 0);
  const parts = includeBilateral ? [
    { key: "cleared", label: "EEX-cleared", value: cleared },
    { key: "physical", label: "Physical", value: physical },
    { key: "otc", label: "OTC financial", value: otc },
  ] : [{ key: "final", label: "Final", value: cell.finalMissingCount ? null : cell.finalEUR },
    ...(includeEstimates ? [{ key: "estimate", label: "Estimate", value: cell.estimateMissingCount ? null : cell.estimateEUR }] : [])];
  return { total, cleared, physical, otc, parts, estimated, missing, forecasts,
    label: includeBilateral ? "Strategy total" : "EEX-cleared P&L", scheduled: cell.scheduledCount > 0,
    stale: forecasts && cell.staleCount > 0, unclassified: includeBilateral ? bilateral.unclassifiedCount : 0,
    blockers: includeBilateral ? bilateral.blockers ?? [] : [],
    physicalEstimated: bilateral.physicalEstimateEUR !== 0, otcEstimated: bilateral.otcEstimateEUR !== 0 };
}
export type MonthlyPnlSummary = ReturnType<typeof monthlyPnlSummary>;

/** Integer cents keep the bridge endpoints identical to the report's totals. */
export function monthlyPnlBridge(summary: MonthlyPnlSummary) {
  if (summary.total === null || summary.parts.some(part => part.value === null)) return null;
  let cents = 0;
  const bars = summary.parts.map(part => {
    const from = cents / 100;
    cents += Math.round(part.value! * 100);
    return { ...part, value: part.value!, from, to: cents / 100, total: false };
  });
  return [...bars, { key: "total", label: summary.label, value: summary.total, from: 0, to: summary.total, total: true }];
}
